import { ApolloQueryResult, useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";

import TertiaryButton from "@components/buttons/TertiaryButton";
import ExpertConsultsTable from "@components/expert/ExpertConsultsTable";
import styles from "@components/expert/ExpertDashboardMetricPage.module.css";
import ExpertKPIsFilters from "@components/forms/ExpertKPIsFilters";
import HandleQuery from "@components/HandleQuery";
import ExpertPage from "@components/pageTypes/ExpertPage";
import Icon from "@components/text/Icon";
import ButtonLink from "@components/typography/ButtonLink";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { TimePeriodEnum } from "@typing/Enums";
import {
  ConsultDurationEnum,
  ConsultPositionEnum,
  ConsultTimePeriodEnum,
  ExpertConsultsSortByEnum,
  ExpertGlobalRoleEnum,
  GetExpertConsultsByAttributeDocument,
  GetExpertConsultsByAttributeQuery,
  GetExpertConsultsByAttributeQueryVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { calculateDatesFromTimePeriod } from "@utils/modelUtils";
import { minutesToHoursAndMinutes } from "@utils/timeUtils";

type Filters = ComponentProps<typeof ExpertKPIsFilters>["filters"];

type ConsultsData = NonNullable<GetExpertConsultsByAttributeQuery["expertConsults"]>;

enum Type {
  DURATION = "duration",
  POSITION = "position"
}

type SectionProps = {
  data: ConsultsData;
  fetchMore: () => Promise<ApolloQueryResult<GetExpertConsultsByAttributeQuery>>;
  setSortBy: (sortBy: ExpertConsultsSortByEnum) => void;
  setSortDirection: (sortDirection: SortDirectionEnum) => void;
  sortBy: ExpertConsultsSortByEnum;
  sortDirection: SortDirectionEnum;
  tableIndex: number;
  type: Type;
};

const Section = ({
  data,
  fetchMore,
  setSortBy,
  setSortDirection,
  sortBy,
  sortDirection,
  tableIndex,
  type
}: SectionProps) => {
  const consults = useNodesFromConnection(data);
  const [loading, setLoading] = useState(false);

  const searchNext = () => {
    setLoading(true);
    fetchMore?.().then(() => {
      setLoading(false);
    });
  };

  return (
    <>
      {data.totalCount > 0 && (
        <>
          <div className={styles.title}>
            <FormattedMessage id={`pages.expert.dashboard.consults.${type}.${tableIndex}`} />
          </div>
          <div className={styles.table}>
            <ExpertConsultsTable
              consults={consults}
              pageInfo={data.pageInfo}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              skipScroll
              sortBy={sortBy}
              sortDirection={sortDirection}
              totalCount={data.totalCount}
              upcoming={ConsultTimePeriodEnum.PAST}
            />
            {data.pageInfo.hasNextPage && (
              <div className="ion-margin-start">
                {loading ? (
                  <Icon icon="spinner" spacingStart />
                ) : (
                  <div>
                    <ButtonLink onClick={searchNext}>
                      <FormattedMessage id="pages.expert.dashboard.consults.loadMore" />
                    </ButtonLink>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

const ExpertDashboardConsultsPage = () => {
  const [sortBy, setSortBy] = useState<ExpertConsultsSortByEnum>(ExpertConsultsSortByEnum.DATE);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.DESCENDING);
  const location = useLocation();
  const intl = useIntl();
  const myExpert = useMyExpert();

  const type = useMemo(() => {
    if (location.pathname.includes(Type.DURATION)) {
      return Type.DURATION;
    }
    return Type.POSITION;
  }, [location.pathname]);

  const [filters, setFilters] = useState<Filters>({
    employerId: null,
    endDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).endDate,
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? null : myExpert?.id,
    selfPaid: false,
    startDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).startDate,
    timePeriod: TimePeriodEnum.SEVEN_DAYS
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [getExpertConsultsFirst, getExpertConsultsFirstQuery] = useLazyQuery<
    GetExpertConsultsByAttributeQuery,
    GetExpertConsultsByAttributeQueryVariables
  >(GetExpertConsultsByAttributeDocument);

  const [getExpertConsultsSecond, getExpertConsultsSecondQuery] = useLazyQuery<
    GetExpertConsultsByAttributeQuery,
    GetExpertConsultsByAttributeQueryVariables
  >(GetExpertConsultsByAttributeDocument);

  const [getExpertConsultsThird, getExpertConsultsThirdQuery] = useLazyQuery<
    GetExpertConsultsByAttributeQuery,
    GetExpertConsultsByAttributeQueryVariables
  >(GetExpertConsultsByAttributeDocument);

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    const variables = {
      employerId: filters.employerId,
      endDate: dates.endDate,
      expertId: filters.expertId,
      forExpert: false,
      selfPaid: filters.selfPaid,
      sortBy,
      sortDirection,
      startDate: dates.startDate,
      timePeriod: ConsultTimePeriodEnum.PAST
    };

    if (myExpert) {
      getExpertConsultsFirst({
        fetchPolicy: "network-only",
        variables: {
          ...variables,
          duration: type === Type.DURATION ? ConsultDurationEnum.FIFTEEN_MINUTES : undefined,
          position: type === Type.POSITION ? ConsultPositionEnum.INITIAL : undefined
        }
      });
      getExpertConsultsSecond({
        fetchPolicy: "network-only",
        variables: {
          ...variables,
          duration: type === Type.DURATION ? ConsultDurationEnum.THIRTY_MINUTES : undefined,
          position: type === Type.POSITION ? ConsultPositionEnum.SECOND : undefined
        }
      });
      getExpertConsultsThird({
        fetchPolicy: "network-only",
        variables: {
          ...variables,
          duration: type === Type.DURATION ? ConsultDurationEnum.ONE_HOUR : undefined,
          position: type === Type.POSITION ? ConsultPositionEnum.THIRD_PLUS : undefined
        }
      });
    }
  }, [
    filters,
    getExpertConsultsFirst,
    getExpertConsultsSecond,
    getExpertConsultsThird,
    intl,
    myExpert,
    sortBy,
    sortDirection,
    type
  ]);

  useSubscribeToJourneys(() => {
    getExpertConsultsFirstQuery.refetch();
    getExpertConsultsSecondQuery.refetch();
    getExpertConsultsThirdQuery.refetch();
  });

  const total =
    type == Type.DURATION
      ? minutesToHoursAndMinutes({
          minutes:
            (getExpertConsultsFirstQuery.data?.expertConsults.totalCount ?? 0) * 15 +
            (getExpertConsultsSecondQuery.data?.expertConsults.totalCount ?? 0) * 30 +
            (getExpertConsultsThirdQuery.data?.expertConsults.totalCount ?? 0) * 60,
          roundRemainder: true
        })
      : null;

  return (
    <ExpertPage
      helmetKey="pageTitles.expert.dashboardConsults.list"
      imageName="peopleCoffeeLight"
      titleKey={`pages.expert.dashboard.consults.${type}.title`}
    >
      <TertiaryButton
        icon="chevronLeft"
        routerLink={`/expert/dashboard${location.search}`}
        textKey="pages.expert.dashboard.consults.dashboard"
      />
      <div className={styles.flex}>
        <div
          className={`${styles.consultFilters} ${
            filters.timePeriod === TimePeriodEnum.CUSTOM ? styles.biggerConsultFilters : ""
          }`}
        >
          <ExpertKPIsFilters filters={filters} onChange={handleFilterChange} showEmployerFilter />
        </div>
        {total && (
          <div className={styles.infoBox}>
            <div className={styles.infoBoxLabel}>
              <FormattedMessage id="pages.expert.dashboard.consults.totalTime" />
            </div>
            <div className={styles.infoBoxData}>{total}</div>
          </div>
        )}
      </div>

      <HandleQuery query={getExpertConsultsFirstQuery}>
        {firstData => (
          <HandleQuery query={getExpertConsultsSecondQuery}>
            {secondData => (
              <HandleQuery query={getExpertConsultsThirdQuery}>
                {thirdData => (
                  <>
                    <Section
                      data={firstData.expertConsults}
                      fetchMore={() =>
                        getExpertConsultsFirstQuery.fetchMore({
                          variables: {
                            cursor: getExpertConsultsFirstQuery.data?.expertConsults.pageInfo.endCursor
                          }
                        })
                      }
                      key={1}
                      setSortBy={setSortBy}
                      setSortDirection={setSortDirection}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      tableIndex={0}
                      type={type}
                    />
                    <Section
                      data={secondData.expertConsults}
                      fetchMore={() =>
                        getExpertConsultsSecondQuery.fetchMore({
                          variables: {
                            cursor: getExpertConsultsSecondQuery.data?.expertConsults.pageInfo.endCursor
                          }
                        })
                      }
                      key={2}
                      setSortBy={setSortBy}
                      setSortDirection={setSortDirection}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      tableIndex={1}
                      type={type}
                    />
                    <Section
                      data={thirdData.expertConsults}
                      fetchMore={() =>
                        getExpertConsultsThirdQuery.fetchMore({
                          variables: {
                            cursor: getExpertConsultsThirdQuery.data?.expertConsults.pageInfo.endCursor
                          }
                        })
                      }
                      key={3}
                      setSortBy={setSortBy}
                      setSortDirection={setSortDirection}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      tableIndex={2}
                      type={type}
                    />
                  </>
                )}
              </HandleQuery>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertPage>
  );
};

export default ExpertDashboardConsultsPage;
